import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { scanStore } from "@/lib/scanStore";
import {
  disposeLocalPhoto,
  fileToAnalyzeDataUrl,
  makeLocalPhoto,
  normalizeHeic,
  uploadPhotos,
  type LocalPhoto,
} from "@/lib/photos";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { isNative, pickNativePhoto, dataUrlToFile } from "@/lib/nativeCamera";
import { useScanBalance } from "@/hooks/useScanBalance";
import { tap } from "@/lib/nativeHaptics";
import { PhotoSourcePicker } from "@/components/PhotoSourcePicker";

// ── Categories ─────────────────────────────────────────────────────────────
const CATEGORIES = [
  "Jacket / Coat",
  "Dress",
  "Top / Blouse",
  "Trousers / Skirt",
  "Knitwear",
  "Shoes",
  "Bag",
  "Accessory",
] as const;
type Category = (typeof CATEGORIES)[number];

// ── Photo slots ────────────────────────────────────────────────────────────
interface SlotDef {
  key: string;
  label: string;
  subtext: string;
  required: boolean;
}
const SLOTS: SlotDef[] = [
  { key: "full",   label: "Full item view",     subtext: "Lay flat or hang — show the whole piece", required: true },
  { key: "label",  label: "Label or markings",  subtext: "Label, stamp, maker's mark, or if absent — a close-up of the care tag", required: true },
  { key: "constr", label: "Care tag or close-up", subtext: "Care label, zipper, seam, or stitching — anything inside", required: true },
  { key: "back",   label: "Back view",          subtext: "Optional but recommended", required: false },
  { key: "extra",  label: "Additional detail",  subtext: "Print, lining, hardware, pattern — anything else worth noting", required: false },
];
const NO_LABEL_SUBTEXT = "Show us the care tag or a close-up instead — seam, hardware, or stitching";
const NO_LABEL_PREFIX  = "Submitter confirms no label present.";

// Brightness check — average pixel brightness 0–255.
function checkBrightness(file: File, cb: (avg: number) => void) {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    try {
      // Cap canvas size — we only need an average, no need to read 12MP.
      const maxDim = 256;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { cb(255); return; }
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      let total = 0;
      for (let i = 0; i < data.length; i += 4) {
        total += (data[i] + data[i + 1] + data[i + 2]) / 3;
      }
      cb(total / (data.length / 4));
    } finally {
      URL.revokeObjectURL(url);
    }
  };
  img.onerror = () => { URL.revokeObjectURL(url); cb(255); };
  img.src = url;
}

const HINTS_MAX = 400;
const LOADING_STAGES = [
  "Reading your item…",
  "Checking close-up details…",
  "Almost there…",
];

export default function Scan() {
  const nav = useNavigate();
  const { user } = useAuth();

  const { balance } = useScanBalance();

  const [category, setCategory] = useState<Category | null>(null);
  // Per-slot photo + brightness warning state, keyed by slot.key.
  const [slotPhotos, setSlotPhotos] = useState<Record<string, LocalPhoto | undefined>>({});
  const [slotDark, setSlotDark] = useState<Record<string, boolean>>({});
  const [noLabel, setNoLabel] = useState(false);
  const [brand, setBrand] = useState<string>("");
  const [hints, setHints] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [loadingIdx, setLoadingIdx] = useState(0);
  // Which slot is currently waiting for the themed photo-source picker
  // to resolve. null = picker closed.
  const [pickerSlot, setPickerSlot] = useState<string | null>(null);

  // One hidden <input> per slot.
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Free object URLs on unmount.
  useEffect(() => {
    return () => {
      Object.values(slotPhotos).forEach((p) => p && disposeLocalPhoto(p));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cycle the inline loading text on submit. Min ~3s on the first two so it
  // doesn't flash; the upload itself usually outlasts the cycle.
  useEffect(() => {
    if (!busy) { setLoadingIdx(0); return; }
    const t1 = setTimeout(() => setLoadingIdx(1), 3000);
    const t2 = setTimeout(() => setLoadingIdx(2), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [busy]);

  // Slot 2 is required by default but becomes optional when "no label" is ticked.
  const slot2Ok = !!slotPhotos["label"] || noLabel;
  const slot1Ok = !!slotPhotos["full"];
  const slot3Ok = !!slotPhotos["constr"];
  const filledRequired = slot1Ok && slot2Ok && slot3Ok;
  const canSubmit = !!category && filledRequired && !busy;

  // Whether to reveal slot 4/5. Shown once all three required are filled.
  const showOptional = filledRequired;

  const handlePick = async (slotKey: string, fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    try {
      const raw = fileList[0];
      const normalized = await normalizeHeic(raw);
      const made = await makeLocalPhoto(normalized);
      setSlotPhotos((prev) => {
        const old = prev[slotKey];
        if (old) disposeLocalPhoto(old);
        return { ...prev, [slotKey]: made };
      });
      void tap();
      checkBrightness(made.file, (avg) => {
        setSlotDark((prev) => ({ ...prev, [slotKey]: avg < 60 }));
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Couldn't read that photo", description: "Try a JPG, PNG, or HEIC.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  // Step 1: tapping a slot's "+" opens our themed picker (PhotoSourcePicker).
  // We don't call into Capacitor yet — the user picks Camera vs Library
  // first via the in-app UI, then runPhotoPick fires the matching iOS
  // surface directly (bypasses iOS's dark-grey system action sheet).
  const handleNativePick = (slotKey: string) => {
    setPickerSlot(slotKey);
  };

  const runPhotoPick = async (slotKey: string, source: "camera" | "library") => {
    setPickerSlot(null);
    setBusy(true);
    try {
      const result = await pickNativePhoto(source);
      if (!result) { setBusy(false); return; }
      const file = dataUrlToFile(result.dataUrl, `photo_${slotKey}.jpg`, result.mimeType);
      const made = await makeLocalPhoto(file);
      setSlotPhotos((prev) => {
        const old = prev[slotKey];
        if (old) disposeLocalPhoto(old);
        return { ...prev, [slotKey]: made };
      });
      void tap();
      checkBrightness(made.file, (avg) => {
        setSlotDark((prev) => ({ ...prev, [slotKey]: avg < 60 }));
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Couldn't read that photo", description: "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const removeSlot = (slotKey: string) => {
    setSlotPhotos((prev) => {
      const old = prev[slotKey];
      if (old) disposeLocalPhoto(old);
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
    setSlotDark((prev) => {
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
  };

  const startVerify = async () => {
    if (!canSubmit || !user) {
      if (!user) toast({ title: "Sign in to scan", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      // Build ordered photo list from the slot definitions.
      const ordered = SLOTS
        .map((s) => ({ slot: s, photo: slotPhotos[s.key] }))
        .filter((x): x is { slot: SlotDef; photo: LocalPhoto } => !!x.photo);

      const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const [analyzeUrls, uploadedUrls] = await Promise.all([
        Promise.all(ordered.map((x) => fileToAnalyzeDataUrl(x.photo.file))),
        uploadPhotos(user.id, sessionId, ordered.map((x) => x.photo)),
      ]);

      // Prepend category + photo-order labels to the details string passed
      // to the AI. Keeps the edge function unchanged while still giving
      // Claude the structured context the new submission UI promises.
      const labelLine = ordered.map((x, i) => `${i + 1}) ${x.slot.label}`).join(", ");
      const userDetails = hints.trim();
      const noLabelPrefix = noLabel ? `${NO_LABEL_PREFIX} ` : "";
      const composed = `${noLabelPrefix}Category: ${category}. Additional details: ${userDetails || "(none)"}. Photos in order: ${labelLine}.`;

      scanStore.setPhoto(analyzeUrls[0]);
      scanStore.setAnalyzeImages(analyzeUrls);
      scanStore.setPhotos(uploadedUrls);
      scanStore.setHints(composed);
      scanStore.setBrand(brand);
      scanStore.setCategory(category || "");
      scanStore.setPhotoLabels(ordered.map((x) => x.slot.label));
      scanStore.clearResult();
      scanStore.clearError();
      scanStore.clearVerifyResult();
      nav("/analyze");
    } catch (e) {
      console.error("[scan upload]", e);
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Upload failed", description: msg || "Check your connection and try again", variant: "destructive" });
      setBusy(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────
  const Pill = ({ value }: { value: Category }) => {
    const selected = category === value;
    return (
      <button
        type="button"
        onClick={() => setCategory(selected ? null : value)}
        disabled={busy}
        aria-pressed={selected}
        style={{
          height: 36, padding: "0 1rem", flexShrink: 0,
          border: "1px solid hsl(var(--ink))",
          background: selected ? "hsl(var(--ink))" : "transparent",
          color: selected ? "hsl(var(--cream))" : "hsl(var(--ink))",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase",
          cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap",
        }}
      >
        {value}
      </button>
    );
  };

  const SlotCard = ({ slot }: { slot: SlotDef }) => {
    const photo = slotPhotos[slot.key];
    const dark = slotDark[slot.key];
    const isLabelSlot = slot.key === "label";
    const effectiveRequired = slot.required && !(isLabelSlot && noLabel);
    const effectiveSubtext = isLabelSlot && noLabel ? NO_LABEL_SUBTEXT : slot.subtext;
    const optionalStyle = isLabelSlot && noLabel;
    return (
      <div style={{
        background: "hsl(var(--cream))",
        border: optionalStyle ? "1px dashed hsl(var(--ink-2))" : "1px solid hsl(var(--ink))",
        padding: 12, display: "flex", flexDirection: "column", gap: 6,
      }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <input
            ref={(el) => { inputRefs.current[slot.key] = el; }}
            type="file"
            accept="image/*,.heic,.heif"
            style={{ display: "none" }}
            onChange={(e) => { handlePick(slot.key, e.target.files); e.target.value = ""; }}
          />
          {photo ? (
            <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0, border: "1px solid hsl(var(--ink))", overflow: "hidden" }}>
              <img src={photo.previewUrl} alt={slot.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <button
                onClick={() => removeSlot(slot.key)}
                disabled={busy}
                aria-label={`Remove ${slot.label}`}
                style={{
                  position: "absolute", top: -6, right: -6, width: 18, height: 18,
                  border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))",
                  color: "hsl(var(--ink))", cursor: busy ? "wait" : "pointer",
                  fontSize: 12, lineHeight: 1, padding: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >×</button>
            </div>
          ) : (
            <button
              onClick={() => isNative ? handleNativePick(slot.key) : inputRefs.current[slot.key]?.click()}
              disabled={busy}
              aria-label={`Add photo for ${slot.label}`}
              style={{
                width: 48, height: 48, flexShrink: 0,
                border: "1.5px dashed hsl(var(--ink))",
                background: "hsl(var(--paper))", color: "hsl(var(--ink))",
                cursor: busy ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {Icon.plus(20)}
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15,
              color: "hsl(var(--ink))", lineHeight: 1.2,
            }}>
              {slot.label}
              {effectiveRequired && (
                <span style={{ color: "hsl(var(--rust))", marginLeft: 4 }}>*</span>
              )}
            </div>
            <div className="text-secondary-foreground" style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              letterSpacing: "0.04em", marginTop: 2,
              lineHeight: 1.4,
            }}>
              {effectiveSubtext}
            </div>
          </div>
        </div>
        {photo && dark && (
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            color: "hsl(var(--rust))", letterSpacing: "0.04em",
            paddingLeft: 60, lineHeight: 1.4,
          }}>
            This photo looks dark — a brighter shot will improve your result.
          </div>
        )}
      </div>
    );
  };

  return (
    <MobileFrame>
      <PhotoSourcePicker
        open={pickerSlot !== null}
        onCamera={() => pickerSlot && runPhotoPick(pickerSlot, "camera")}
        onLibrary={() => pickerSlot && runPhotoPick(pickerSlot, "library")}
        onCancel={() => setPickerSlot(null)}
      />
      <div style={{
        minHeight: "100vh", background: "hsl(var(--paper))",
        padding: "60px 20px 120px", display: "flex", flexDirection: "column", gap: 16,
      }}>
        {/* Top bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav("/home")} aria-label="Close" style={{
            width: 36, height: 36, border: "1px solid hsl(var(--ink))",
            background: "transparent", color: "hsl(var(--ink))", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{Icon.x(16)}</button>
          <div style={{ textAlign: "center" }}>
            <div className="caps" style={{ color: "hsl(var(--rust))" }}>FIG Verify</div>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 18, color: "hsl(var(--ink))" }}>Submit an item</div>
          </div>
          <div style={{ width: 36, height: 36 }} />
        </div>

        {/* Scans remaining pill */}
        {balance && (() => {
          const PAID = new Set(["collector", "seller", "pro_seller"]);
          const sub = PAID.has(balance.subscriptionTier) &&
            (balance.subscriptionStatus === "active" || balance.subscriptionStatus === "trialing");
          const free = balance.freeRemaining;
          const credits = balance.scanCredits;
          const subRemaining = balance.totalRemaining - credits - (free > 0 ? free : 0);
          let text: string;
          let warn = false;
          if (balance.totalRemaining === 0) {
            text = "no scans remaining";
            warn = true;
          } else if (sub && balance.subscriptionMonthlyCap > 0) {
            const monthlyLeft = Math.max(0, subRemaining);
            text = `${monthlyLeft} / ${balance.subscriptionMonthlyCap} remaining`;
            warn = monthlyLeft <= 1;
          } else {
            const n = free > 0 ? free : credits;
            text = n === 1 ? "1 scan left" : `${n} scans left`;
            warn = n <= 1;
          }
          return (
            <div style={{
              alignSelf: "flex-end",
              padding: "3px 10px",
              // Stronger contrast: --ink (primary text) instead of --ink-2
              // (secondary). On Velvet Underground the dim secondary color
              // disappeared into the dark paper bg; --ink is the guaranteed
              // AA-readable primary color on paper across every theme.
              border: `1px solid ${warn ? "hsl(var(--rust))" : "hsl(var(--ink))"}`,
              background: warn ? "hsl(var(--rust))" : "transparent",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
              color: warn ? "hsl(var(--rust-fg))" : "hsl(var(--ink))",
            }}>
              {text}
            </div>
          );
        })()}

        {/* Category selector */}
        <div>
          <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
            Category <span style={{ color: "hsl(var(--ink))" }}>*</span>
          </div>
          <style>{`
            .fig-cat-scroll { scrollbar-width: thin; scrollbar-color: hsl(var(--ink)) transparent; }
            .fig-cat-scroll::-webkit-scrollbar { height: 4px; }
            .fig-cat-scroll::-webkit-scrollbar-track { background: hsl(var(--paper-3)); }
            .fig-cat-scroll::-webkit-scrollbar-thumb { background: hsl(var(--ink)); border-radius: 2px; }
          `}</style>
          <div style={{ position: "relative" }}>
            <div className="fig-cat-scroll" style={{
              display: "flex", gap: 8, overflowX: "scroll", flexWrap: "nowrap",
              paddingBottom: 8, marginLeft: -4, paddingLeft: 4, paddingRight: 4,
            }}>
              {CATEGORIES.map((c) => <Pill key={c} value={c} />)}
            </div>
            {/* Edge fades — hint that the row scrolls horizontally. */}
            <div aria-hidden style={{
              position: "absolute", top: 0, bottom: 8, left: 0, width: 24,
              background: "linear-gradient(to right, hsl(var(--paper)), hsl(var(--paper) / 0))",
              pointerEvents: "none",
            }} />
            <div aria-hidden style={{
              position: "absolute", top: 0, bottom: 8, right: 0, width: 24,
              background: "linear-gradient(to left, hsl(var(--paper)), hsl(var(--paper) / 0))",
              pointerEvents: "none",
            }} />
          </div>
        </div>

        {/* Photo slots */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>Photos</div>
          <SlotCard slot={SLOTS[0]} />
          <SlotCard slot={SLOTS[1]} />
          <label style={{
            display: "flex", alignItems: "center", gap: 8,
            paddingLeft: 4, marginTop: -2, cursor: "pointer",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            letterSpacing: "0.04em", color: "hsl(var(--ink))",
          }}>
            <input
              type="checkbox"
              checked={noLabel}
              onChange={(e) => setNoLabel(e.target.checked)}
              disabled={busy}
              style={{ accentColor: "hsl(var(--ink))" }}
            />
            No label on this item
          </label>
          <SlotCard slot={SLOTS[2]} />
          {showOptional && SLOTS.slice(3).map((s) => <SlotCard key={s.key} slot={s} />)}
        </div>

        {/* Optional details */}
        <div style={{
          background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
          padding: 12, display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>
            Details <span className="text-secondary-foreground">(optional)</span>
          </div>

          {/* Brand — separate field so AI treats it as a hard constraint */}
          <div>
            <label htmlFor="scan-brand" className="caps" style={{ display: "block", marginBottom: 4, fontSize: 9, color: "hsl(var(--ink-2))" }}>
              Brand
            </label>
            <input
              id="scan-brand"
              type="text"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              disabled={busy}
              placeholder="e.g. Peck and Peck, Levi's, Christian Dior"
              style={{
                width: "100%", boxSizing: "border-box", padding: "8px 10px",
                border: "1px dashed hsl(var(--ink))", background: "hsl(var(--paper))",
                color: "hsl(var(--ink))", fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, outline: "none",
              }}
            />
          </div>

          {/* Free-text notes */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <label htmlFor="scan-hints" className="caps" style={{ fontSize: 9, color: "hsl(var(--ink-2))" }}>
                Notes
              </label>
              <div className="text-secondary-foreground" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9 }}>
                {hints.length}/{HINTS_MAX}
              </div>
            </div>
            <textarea
              id="scan-hints"
              value={hints}
              onChange={(e) => setHints(e.target.value.slice(0, HINTS_MAX))}
              disabled={busy}
              placeholder="Era, where you found it, anything else that might help"
              rows={2}
              style={{
                width: "100%", boxSizing: "border-box", padding: "8px 10px",
                border: "1px dashed hsl(var(--ink))", background: "hsl(var(--paper))",
                color: "hsl(var(--ink))", fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, lineHeight: 1.35, resize: "vertical", minHeight: 48, outline: "none",
              }}
            />
          </div>
        </div>

        {/* Submit / inline loading */}
        <div style={{ marginTop: 4 }}>
          {busy ? (
            <div style={{
              padding: "16px",
              border: "1px solid hsl(var(--ink))",
              background: "hsl(var(--cream))",
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 16, color: "hsl(var(--ink))",
              textAlign: "center",
            }}>
              {LOADING_STAGES[loadingIdx]}
            </div>
          ) : (
            <button
              onClick={startVerify}
              disabled={!canSubmit}
              className="btn btn-rust"
              style={{
                width: "100%", padding: "16px", fontSize: 16,
                opacity: canSubmit ? 1 : 0.45,
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
            >
              Verify this item
            </button>
          )}
        </div>
      </div>
    </MobileFrame>
  );
}
